import { toastSuccess } from "./messages";
import { refreshModal } from "./modal";
import { newsletterwarnings } from "../features/newsletter";
import { variantoptionssort } from "../features/variant";
import { updateproductimagessort } from "../features/productImages";
import { saveordercollcat, savefeaturedartistsorder } from "../features/collections";
import { earningcalcrev, calcPricing } from "../features/pricing";
import { findprevnext } from "../features/navigation";
import { setCookie } from "../features/cookies";
import {
  directoryUploadS3Done,
  directoryUploadError,
  directoryUploadStarted,
  directoryUploadThumb,
  directoryUploadProgress,
} from "../features/directoryUpload";
import {
  initCoverUpload,
  initBriefUpload,
  initNlImageUpload,
  initProfilePicUpload,
  initIeClientUpload,
} from "../features/presignedDropzones";
import { api } from "../api";
import { setHtmleditor } from "../globalState";
import { getDomElement, fadeIn } from "../utils/dom";
import { makeSortable } from "../plugins/sortable";
import type Sortable from "sortablejs";
import Dropzone from "dropzone";
import { initMarketingUpload } from "../features/marketingUpload";
// NOTE: ckeditor5 is deliberately NOT imported here — see initBiographyEditor().

export function modalPostLoad(whatParts: string[]): void {
  const modalType = whatParts[0];

  // --- Dropzone initializations ---

  if (modalType === "ieclient") {
    initIeClientUpload();
  }

  if (modalType === "marketing") {
    // Initialization handled by MarketingModal.tsx useEffect, which is the only place that
    // holds the `reload` for its file list. Initialising here too attached a SECOND Dropzone
    // to #marketingupload with no success callback — so uploads worked but the list never
    // refreshed until the modal was reopened.
  }

  if (modalType === "editbrief") {
    initBriefDropzone(whatParts[1] ?? "");
  }

  if (
    modalType === "editnewsletter" ||
    modalType === "editnewsletter-artist" ||
    modalType === "editnewsletter-artist2"
  ) {
    setTimeout(() => fadeIn("editnewsletter-preview"), 500);
    void newsletterwarnings();
    initNlUploadDropzone();
  }

  if (modalType === "selectrecipients") {
    void newsletterwarnings();
  }

  if (modalType === "profilepic") {
    initProfilePicDropzone();
  }

  // "settings" profile pic dropzone is handled by SettingsModal.tsx

  if (modalType === "biography") {
    // Initialization handled by BiographyModal.tsx useEffect
  }

  if (modalType === "directory") {
    initDirectoryUploadDropzone();
  }

  if (modalType === "editcollection") {
    // Initialization handled by EditCollectionModal.tsx useEffect, which is the
    // only place that knows which collection the cover belongs to.
  }

  // --- Sortable initializations ---

  if (modalType === "editvariant") {
    initSortable("#ecommerce-variantoptions", {
      onEnd: () => void variantoptionssort(whatParts[1] ?? ""),
    });
  }

  if (modalType === "addproduct") {
    for (const suffix of ["p", "s", "l"]) {
      initSortable(`#addproduct_images_${suffix}_target`, {
        onEnd: () => {
          updateproductimagessort(suffix);
        },
      });
    }
  }

  if (modalType === "admin" && whatParts[1] === "collections") {
    initSortable("#collection_categories", {
      filter: "div.collection_categories_category_allcoll, span.button",
      onEnd: () => void saveordercollcat(),
    });
  }

  if (modalType === "reorderinteriors") {
    if (initSortable("#interiorreorderdiv")) {
      getDomElement("interiorreorderdiv")?.style.setProperty("user-select", "none");
    }
  }

  if (modalType === "reorderlist") {
    if (initSortable("#reorderlist-artworks")) {
      getDomElement("reorderlist-artworks")?.style.setProperty("user-select", "none");
    }
  }

  if (modalType === "artistfeatures") {
    initSortable("#featured_artists", {
      filter: "div.collection_categories_category_allcoll, span.button",
      onEnd: () => void savefeaturedartistsorder(),
    });
  }

  // --- Pricing slider ---

  if (modalType === "pricing") {
    const slider = getDomElement<HTMLInputElement>("markup-" + (whatParts[1] ?? ""));
    if (slider) {
      slider.oninput = function () {
        const earning_recalc = earningcalcrev(Number((this as HTMLInputElement).value));
        const el = getDomElement("current-markup2-" + (whatParts[1] ?? ""));
        if (el) el.innerHTML = String(earning_recalc);
        calcPricing(whatParts[1] ?? "");
      };
    }
  }

  // --- Focus handlers ---

  // signup step3: SignupModal focuses its first field via a ref on mount.

  // login: LoginModal focuses its own email field via a ref on mount.

  if (modalType === "contact") {
    getDomElement("contact_name")?.focus();
  }

  // newcollcat: NewCollCatModal focuses its own name field via `autoFocus`. The
  // `getDomElement("collcat_name")` call that used to be here reached the EDIT modal's id,
  // which is a different modal, and was removed with it.

  if (modalType === "editlist") {
    getDomElement("edit_lightbox_title")?.focus();
  }

  // --- View modal ---

  if (modalType === "view") {
    findprevnext();
    getDomElement("viewer-img")?.addEventListener("dragstart", function (event) {
      event.preventDefault();
    });
  }

  // --- Cookie ---

  if (modalType === "notforpictufy") {
    setCookie("PictufyNoSignup", "1", 100000);
  }

  // --- hCaptcha ---

  if (modalType === "signup" && whatParts[1] === "step0") {
    const hcaptcha = window.hcaptcha;
    if (hcaptcha) {
      hcaptcha.render("captcha", {
        sitekey: "a8d50e3c-84b1-4b5f-bedf-0c66acf18a44",
        theme: "light",
        "error-callback": "onError",
        callback: "captchaFinished",
      });
    }
  }
}

// ---- Helper functions ----

/** Returns false when the element isn't on the page (callers guard follow-up styling). */
function initSortable(selector: string, options?: Sortable.Options): boolean {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) return false;
  makeSortable(el, options);
  return true;
}

// ---- Dropzone initializers ----

export function initMarketingDropzone(onSuccess?: () => void): void {
  initMarketingUpload(onSuccess);
}

export function initBriefDropzone(briefId: string, onSuccess?: () => void): void {
  initBriefUpload(briefId, onSuccess);
}

export function initNlUploadDropzone(): void {
  initNlImageUpload();
}

export function initProfilePicDropzone(onSuccess?: () => void): void {
  initProfilePicUpload("profilepicupload2", {
    onSuccess: () => {
      refreshModal();
      toastSuccess("Your profile photo is uploaded");
      if (onSuccess) onSuccess();
    },
  });
}

export function initBiographyDropzones(onPicSuccess?: (imgPath: string) => void): void {
  initProfilePicUpload("profilepicupload2", {
    onSuccess: (imgid) => {
      if (onPicSuccess) {
        onPicSuccess(imgid);
        return;
      }
      refreshModal();
      toastSuccess("Your biography photo is updated");
    },
  });

  const profilePicIdEl = getDomElement<HTMLInputElement>("profile_pic_id");
  if (profilePicIdEl) {
    initProfilePicUpload("profilepicupload3", {
      userId: Number(profilePicIdEl.value),
      onSuccess: () => {
        refreshModal();
        toastSuccess("Your biography photo is updated");
      },
    });
  }
}

/**
 * Mount the rich-text editor on the artist biography modal.
 *
 * CKEditor is by far the heaviest thing this bundle can reach — ~680 kB of JS
 * plus a ~380 kB skin — and this one modal is its only consumer, so both are
 * loaded on demand rather than shipped to every visitor. The `import()` calls
 * become their own chunks; the CSS import is what keeps the skin out of the
 * single stylesheet built by vite.styles.config.ts.
 */
export function initBiographyEditor(onData?: (html: string) => void): void {
  const editorEl = document.querySelector("#biography-editor");
  if (!editorEl) return;

  void (async () => {
    try {
      const [
        { ClassicEditor, Essentials, Bold, Italic, Underline, Strikethrough, Font, Paragraph },
      ] = await Promise.all([import("ckeditor5"), import("ckeditor5/ckeditor5.css")]);

      const newEditor = await ClassicEditor.create(editorEl as HTMLElement, {
        plugins: [Essentials, Bold, Italic, Underline, Strikethrough, Font, Paragraph],
        toolbar: ["undo", "redo", "|", "bold", "italic", "underline", "strikethrough"],
      });

      setHtmleditor(newEditor);
      // CKEditor is not a Preact widget, so its content changes have to be
      // pushed back out; the caller turns them into state.
      onData?.(newEditor.getData());
      newEditor.model.document.on("change:data", () => {
        onData?.(newEditor.getData());
      });
    } catch (error: unknown) {
      console.error("CKEditor initialization failed:", error);
    }
  })();
}

export function initDirectoryUploadDropzone(): void {
  if (!getDomElement("directory-upload")) return;

  // Called from BOTH this file's modal hook and DirectoryModal's own effect (which re-runs
  // whenever its data reloads), and Dropzone throws when a second instance is attached to the
  // same element. Drop any previous one first; `forElement` throws when there is none, which
  // is the normal first-attach path.
  try {
    Dropzone.forElement("#directory-upload").destroy();
  } catch {
    // No Dropzone attached yet.
  }

  // Presigned-PUT flow: the browser uploads the raw JPEG straight to S3 (no size
  // limit on the web server). `accept` fetches a presigned URL per file; the PUT
  // body is the raw file; on success the backend runs the resize Lambda + stores
  // metadata. Only JPEGs are allowed.
  new Dropzone("#directory-upload", {
    url: (files: DropzoneFile[]) => files[0]?.uploadUrl ?? "",
    method: "PUT",
    maxFilesize: 5000,
    acceptedFiles: "image/jpeg",
    createImageThumbnails: true,
    addedfile: directoryUploadStarted,
    thumbnail: directoryUploadThumb,
    uploadprogress: directoryUploadProgress,
    accept: (file: DropzoneFile, done: (error?: string) => void) => {
      if (file.type !== "image/jpeg") {
        done("Only JPEG files are allowed");
        return;
      }
      void (async () => {
        try {
          const data = await api.artwork.directoryUploadPresign({ filename: file.name });
          if (data.url && data.uuid) {
            file.uploadUrl = data.url;
            file.uploadUuid = data.uuid;
            done();
          } else {
            done("Could not prepare upload");
          }
        } catch (err) {
          done((err as Error).message || "Could not prepare upload");
        }
      })();
    },
    success: (file: DropzoneFile) => {
      void directoryUploadS3Done(file);
    },
    error: directoryUploadError,
  });

  // Dropzone 5.x always builds a multipart/form-data body, which S3 would store
  // verbatim (boundary wrapper included) and break image decoding. Override the
  // request hook so the raw file bytes are sent as the PUT body instead.
  const dz = Dropzone.forElement("#directory-upload");
  dz.submitRequest = function (
    xhr: XMLHttpRequest,
    _formData: FormData,
    files: DropzoneFile[]
  ): void {
    xhr.send(files[0]);
  };
}

export function initCoverUploadDropzone(collectionId: string, onSuccess?: () => void): void {
  initCoverUpload(collectionId, onSuccess);
}
